// External libraries
import { unmountComponentAtNode } from 'react-dom';
import { overrideBrowserHistory } from 'yoda-core-components/lib/navigation/history/getBrowserHistory';

// Internal modules
import onJcpLogout from 'yoda-site-components/lib/auth/onJcpLogout';
import windowOnError from 'yoda-site-components/lib/analytics/windowOnError';
import StoreFactory from './redux/storeFactory';
import ABTestHelper from './helpers/ABTest';
import installWebView from './webview/webviewInstall';
import renderApp from './render';

// Site-specific components and functionality

export default ({ App, rootSaga, reducers, middlewares, restClient, micrositeParams }) => {
    window.__PRELOADED_STATE__.abTestEngagements = ABTestHelper.getABTestData(
        window.testVersion,
        true
    );
    const store = StoreFactory(reducers, window.__PRELOADED_STATE__, rootSaga, middlewares);
    const container = document.getElementById('content');
    const { micrositeName } = micrositeParams;
    const state = store.getState();
    // Sometimes you can override the API host name using custom header X-YODA-API-HOST. In such
    // scenario server would hint that putting the host in to the store. If it was overriden
    // then client also would use the same host so that it is consistent both server side and client side
    if (!state?.context?.hostname) {
        const port = window.location.port ? `:${window.location.port}` : '';
        state.context.hostname = `${window.location.hostname}${port}`;
    }
    const {
        hostname = '',
        reqId = '',
        apiPrefixInternal = '',
        apiVersion = null,
        featureFlags = {},
        customErrorPageComponent,
        apiHosts,
        staticHosts,
        deviceType,
        isPreview,
        tzOffset,
        staticRoute,
        staticRoutes,
        isFusionEnabled,
        baseName,
        isNative = false,
        preferences,
    } = state.context;

    // Add basename support in case it is not forbidden via feature flag
    if (!featureFlags?.excludeBaseName ?? false) {
        overrideBrowserHistory({
            basename: (baseName || '').replace(window.location.origin, '').slice(0, -1) || '',
        });
    }

    const context = {
        isBrowser: true, // Set the browser as the default context
        isSecure: window.location.protocol === 'https:',
        hostname,
        apiPrefixInternal,
        apiVersion,
        featureFlags,
        deviceType,
        isPreview,
        tzOffset,
        staticRoute,
        staticRoutes,
        isFusionEnabled,
    };

    const cloudVersion = preferences?.nativeCloud ?? '';
    restClient.setContext({
        isBrowser: context.isBrowser,
        isPreview: context.isPreview,
        tzOffset: context.tzOffset,
        isSecure: context.isSecure,
        hostname: context.hostname, // TODO:PR: If we ever think about using different hostname on server side. This line requires design refactoring!!
        apiPrefixInternal: context.apiPrefixInternal,
        apiVersion: context.apiVersion,
        reqId,
        apiHosts,
        staticHosts,
        deviceType,
        isNative,
        cloudVersion,
    });

    /**
     * Create jcpWebApp object to interact with web and native app,
     * appSensor will handle listener and emit events between web and native app.
     */
    if (isNative) {
        installWebView(store);
    }
    renderApp(App, state.ErrorPage, context, container, customErrorPageComponent, store);

    // This is needed in case you have a client critical error and you'd like to show ErrorPage immediately
    let currentErrorValue = state.ErrorPage;
    store.subscribe(() => {
        const newErrorValue = store?.getState()?.ErrorPage;

        if (newErrorValue !== currentErrorValue) {
            currentErrorValue = newErrorValue;
            if (newErrorValue !== null) {
                unmountComponentAtNode(container);
                renderApp(App, newErrorValue, context, container, customErrorPageComponent, store);
            }
        }
    });

    if (featureFlags.disableOnJcpLogout !== true) {
        onJcpLogout(store);
    }

    if (featureFlags.enableAbsenseOfWindowOnErrorLogs !== true) {
        windowOnError(micrositeName, store);
    }
};
